import { Button, Modal, Flex, Form } from 'antd';
import React, { useCallback, useState, useEffect, useMemo, lazy, Suspense } from 'react';
import DiscriptionSection from '../DiscriptionSection/DiscriptionSection';
import ResponseSection from '../ResponseSection/ResponseSection';
import {
  EndpointHeader,
  EndpointMethodPath,
  AuthorizationWidget,
  ParameterWidget,
  EndpointRequestSection,
  EndpointResponseSection,
} from './components';
import useOpenApiParser from '../../../hooks/useOpenApiParser';
import './EndpointSection.css';

// Lazy load the ApiTestSection component
const ApiTestSection = lazy(() => import('../ApiTestSection/ApiTestSection'));

const EndPointSection = (props) => {
  const [open, setOpen] = useState(false);

  const showDrawer = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const onClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const { servers, path, method, operation, responseCode, setResponseCode, spec } = props;
  const { parseSecurityRequirements, parseEndpointUrl, parsePathParameters, parseRequestBody } = useOpenApiParser();

  // Extract category from tags or use default
  const category = operation?.tags?.[0] || 'API';

  // Parse security requirements using the hook
  const securityRequirements = parseSecurityRequirements(spec, operation?.security);

  // Parse endpoint URL (server + path with variables)
  const endpointUrl = parseEndpointUrl(spec, path);
  const pathParameters = parsePathParameters(spec, operation?.parameters);
  const requestBody = parseRequestBody(spec, operation?.requestBody);

  // Check if there's any content to render in request section
  const hasRequestContent = useMemo(() => {
    return (
      securityRequirements?.length > 0 ||
      (pathParameters?.path && pathParameters.path.length > 0) ||
      (pathParameters?.query && pathParameters.query.length > 0) ||
      (pathParameters?.header && pathParameters.header.length > 0) ||
      (pathParameters?.cookie && pathParameters.cookie.length > 0) ||
      (requestBody && requestBody.length > 0)
    );
  }, [securityRequirements, pathParameters, requestBody]);

  return (
    <div className="endpoint-content">
      {/* Left Column - Main Content (70%) */}
      <div className="endpoint-left-column">
        {/* Header Section with Try it out button */}
        <Flex
          gap="middle"
          vertical={false}
          justify="space-between"
          align="flex-start"
          className="endpoint-header-wrapper"
        >
          <div style={{ flex: 1 }}>
            <EndpointHeader category={category} title={operation?.summary} description={operation?.description} />
            <EndpointMethodPath method={method} endpointUrl={endpointUrl} />
          </div>
          {/* <Button type="primary" ghost onClick={showDrawer} className="endpoint-try-button">
            Try it out
          </Button> */}
        </Flex>

        {hasRequestContent && (
          <div className="request-section">
            {/* <h2 className="section-heading">Request</h2> */}

            {/* Authorization Widget */}
            <AuthorizationWidget securityRequirements={securityRequirements} />

            {/* Path Parameters */}
            {pathParameters?.path && pathParameters.path.length > 0 && (
              <ParameterWidget title="Path Parameters" parameters={pathParameters.path} />
            )}

            {/* Query Parameters */}
            {pathParameters?.query && pathParameters.query.length > 0 && (
              <ParameterWidget title="Query Parameters" parameters={pathParameters.query} />
            )}

            {/* Header Parameters */}
            {pathParameters?.header && pathParameters.header.length > 0 && (
              <ParameterWidget title="Header Parameters" parameters={pathParameters.header} />
            )}

            {/* Cookie Parameters */}
            {pathParameters?.cookie && pathParameters.cookie.length > 0 && (
              <ParameterWidget title="Cookie Parameters" parameters={pathParameters.cookie} />
            )}

            {/* Request Body */}
            {requestBody && requestBody.length > 0 && (
              <>
                {requestBody.map((bodyItem, index) => (
                  <ParameterWidget key={bodyItem.mediaType || index} title="Body" parameters={bodyItem.properties} />
                ))}
              </>
            )}
          </div>
        )}

        <ResponseSection
          data={operation?.responses}
          responseCode={responseCode}
          setResponseCode={setResponseCode}
          spec={spec}
        />
      </div>

      {/* Right Column - Request/Response (30%) */}
      <div className="endpoint-right-column">
        <EndpointRequestSection
          method={method}
          endpoint={path}
          parameters={operation?.parameters || []}
          requestBody={operation?.requestBody || {}}
          fullSpec={spec}
          serverUrl={servers?.[0]?.url || ''}
        />

        <EndpointResponseSection
          method={method}
          endpoint={path}
          parameters={operation?.parameters || []}
          requestBody={operation?.requestBody || {}}
          fullSpec={spec}
          serverUrl={servers?.[0]?.url || ''}
          onOpenModal={showDrawer}
        />
      </div>

      {/* Modal for Try it out - Keep existing modal functionality */}
      <Modal
        title={null}
        open={open}
        onCancel={onClose}
        footer={null}
        width={1040}
        className="api-test-modal"
        closeIcon={null}
      >
        <div className="api-test-modal-header">
          <EndpointMethodPath method={method} endpointUrl={endpointUrl} />
          <button className="modal-close-button" onClick={onClose}>
            <span className="close-icon">×</span>
          </button>
        </div>
        <Suspense fallback={<div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>}>
          <ApiTestSection
            endpoint={path}
            servers={servers}
            operation={operation}
            parameters={operation?.parameters}
            method={method}
            requestBody={operation?.requestBody}
            fullSpec={spec}
          />
        </Suspense>
      </Modal>
    </div>
  );
};

export default EndPointSection;
